import { useDataFetchingClient } from 'data-fetching-client';
import { GET_PUBLIC_PRODUCTS_V2_QUERY } from 'pricing-pages-ui-public-app/__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc.dfc.new';
import { useGetPublicProductGroupsQuery } from 'pricing-pages-ui-public-app/__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc.hooks';
import Raven from 'raven-js';
import { useEffect, useMemo, useRef } from 'react';
import { DEFAULT_CHIRP_TIMEOUT } from '../constants/chirpDefaults';
import { useCurrency } from '../context/CurrencyContext';
import { buildPricingPageViewModel } from '../utils/buildPricingPageViewModel';
import { isCountryCodeString } from '../utils/currencyTypes';
import { getBrowserLocale, getCountryCode } from '../utils/getPreferredLanguage';
import { getProductGroupSkeletonCount } from '../utils/getProductGroupSkeletonCount';
import { isAbortedQueryError } from '../utils/isAbortedQueryError';
const EMPTY_CARDS = [];
const EMPTY_STANDALONE = [];
const EMPTY_SCHEMA_PRODUCTS = [];
export function usePublicProductGroupsData(productGroup, opts) {
  var _viewModel$cards, _viewModel$cardCount, _viewModel$standalone, _viewModel$schemaProd, _viewModel$currencyCo, _viewModel$hasStarter;
  const {
    selectedTerm,
    isEnterpriseTier
  } = opts;
  const {
    displayedCurrencyCode
  } = useCurrency();
  const browserLocale = useMemo(() => getBrowserLocale(), []);
  const rawCountryCode = useMemo(() => getCountryCode(), []);
  const preferredCountry = useMemo(() => isCountryCodeString(rawCountryCode) ? rawCountryCode : undefined, [rawCountryCode]);
  const {
    data,
    error
  } = useGetPublicProductGroupsQuery({
    variables: {
      productGroup,
      currencyCode: displayedCurrencyCode,
      preferredCountry,
      browserLocale
    },
    context: {
      timeout: DEFAULT_CHIRP_TIMEOUT
    }
  });

  /* Track which error instance was last reported so a variable change
   * (e.g. currency picker) that re-runs this effect while the same error
   * is still in state doesn't double-report. */
  const reportedErrorRef = useRef(undefined);
  useEffect(() => {
    if (error !== reportedErrorRef.current) {
      reportedErrorRef.current = error;
      if (error && !isAbortedQueryError(error)) {
        Raven.captureException(error, {
          fingerprint: ['failed-to-fetch-public-product-groups'],
          extra: {
            productGroup,
            currencyCode: displayedCurrencyCode,
            preferredCountry,
            browserLocale
          }
        });
      }
    }
  }, [error, productGroup, displayedCurrencyCode, preferredCountry, browserLocale]);

  /*
   * The purchase-motion CTAs in the merch cards and feature table each call
   * getPublicProductsV2 for their own product (via useSignupContext in
   * purchase-motions-components), firing one network request per product. That
   * metadata is already returned here as data.products, so we prime the
   * getPublicProductsV2 Apollo cache with it and the per-product cache-first
   * reads skip the network.
   *
   * This is done here (the page-level data hook) rather than in any one section
   * so the cache is warm before any CTA mounts. Priming lower in the tree races
   * the CTAs: a section that renders after the prime is safe, but a sibling that
   * renders before it (e.g. merch cards above the feature table) would read a
   * cold cache and fetch. The write runs during render, not in an effect,
   * because child effects fire before parent effects.
   *
   * SlimProductSku is a subset of ProductSku; the missing fields are not read on
   * public pages today, where useSignupContext only consumes skuCategoryName and
   * productLevel.
   */
  const client = useDataFetchingClient();
  const primedProductsRef = useRef(null);
  const products = data === null || data === void 0 ? void 0 : data.products;
  // eslint-disable-next-line react-hooks/refs
  if (products && primedProductsRef.current !== products) {
    // eslint-disable-next-line react-hooks/refs
    primedProductsRef.current = products;
    client.writeQuery({
      query: GET_PUBLIC_PRODUCTS_V2_QUERY,
      variables: {
        productApiNames: Object.keys(products)
      },
      data: {
        result: {
          productsByName: products
        }
      }
    });
  }
  const viewModel = useMemo(() => {
    if (!data) return null;
    return buildPricingPageViewModel(data, {
      selectedTerm,
      isEnterpriseTier,
      displayedCurrencyCode,
      productGroup
    });
  }, [data, selectedTerm, isEnterpriseTier, displayedCurrencyCode, productGroup]);
  let status = 'loading';
  if (error) {
    status = 'error';
  } else if (data) {
    status = 'ready';
  }
  return {
    rawData: data,
    status,
    error,
    cards: (_viewModel$cards = viewModel === null || viewModel === void 0 ? void 0 : viewModel.cards) !== null && _viewModel$cards !== void 0 ? _viewModel$cards : EMPTY_CARDS,
    cardCount: (_viewModel$cardCount = viewModel === null || viewModel === void 0 ? void 0 : viewModel.cardCount) !== null && _viewModel$cardCount !== void 0 ? _viewModel$cardCount : isEnterpriseTier ? 2 : getProductGroupSkeletonCount(productGroup),
    standaloneProducts: (_viewModel$standalone = viewModel === null || viewModel === void 0 ? void 0 : viewModel.standaloneProducts) !== null && _viewModel$standalone !== void 0 ? _viewModel$standalone : EMPTY_STANDALONE,
    schemaProducts: (_viewModel$schemaProd = viewModel === null || viewModel === void 0 ? void 0 : viewModel.schemaProducts) !== null && _viewModel$schemaProd !== void 0 ? _viewModel$schemaProd : EMPTY_SCHEMA_PRODUCTS,
    currencyCode: (_viewModel$currencyCo = viewModel === null || viewModel === void 0 ? void 0 : viewModel.currencyCode) !== null && _viewModel$currencyCo !== void 0 ? _viewModel$currencyCo : displayedCurrencyCode,
    starterApiName: viewModel === null || viewModel === void 0 ? void 0 : viewModel.starterApiName,
    professionalApiName: viewModel === null || viewModel === void 0 ? void 0 : viewModel.professionalApiName,
    enterpriseApiName: viewModel === null || viewModel === void 0 ? void 0 : viewModel.enterpriseApiName,
    hasStarterTier: (_viewModel$hasStarter = viewModel === null || viewModel === void 0 ? void 0 : viewModel.hasStarterTier) !== null && _viewModel$hasStarter !== void 0 ? _viewModel$hasStarter : false
  };
}